/**
 * The outline of a bill of quantities or a text library in the browser:
 * levels for indenting, the block of a title, and OZ suggestions (AP-113).
 */
interface Row {
  id: string;
  parentId: string | null;
}

interface LvRow extends Row {
  kind: 'title' | 'position' | 'note';
  oz: string;
}

/** How many titles stand above each row (0 for the top level). */
export function depths(rows: readonly Row[]): Map<string, number> {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const out = new Map<string, number>();
  for (const r of rows) {
    let d = 0;
    let p = r.parentId ? byId.get(r.parentId) : undefined;
    while (p && d < 20) { d++; p = p.parentId ? byId.get(p.parentId) : undefined; }
    out.set(r.id, d);
  }
  return out;
}

/** The last row of a title's block, its subtitles included (the title itself when empty). */
export function blockEnd<T extends Row>(rows: readonly T[], titleId: string): T | undefined {
  const start = rows.findIndex((r) => r.id === titleId);
  if (start < 0) return undefined;
  const inBlock = new Set([titleId]);
  let end = start;
  rows.forEach((r, i) => {
    if (i > start && r.parentId && inBlock.has(r.parentId)) {
      inBlock.add(r.id);
      end = i;
    }
  });
  return rows[end];
}

/**
 * A sensible next OZ: titles 01 → 02, subtitles <title>.01 → <title>.02
 * (with `parent`), positions <title>.0010 → <title>.0020.
 */
export function suggestOz(items: readonly LvRow[], after: LvRow | undefined, kind: 'title' | 'position', parent?: LvRow | null): string {
  const titles = items.filter((i) => i.kind === 'title');
  if (kind === 'title') {
    const lead = parent ? `${parent.oz}.` : '';
    const own = titles.filter((t) => t.parentId === (parent?.id ?? null));
    const max = own.reduce((m, t) => Math.max(m, Number(t.oz.startsWith(lead) ? t.oz.slice(lead.length) : t.oz) || 0), 0);
    return `${lead}${String(max + 1).padStart(2, '0')}`;
  }
  const title = after ? (after.kind === 'title' ? after : items.find((i) => i.id === after.parentId)) : titles[titles.length - 1];
  const prefix = title?.oz ?? '01';
  const siblings = items.filter((i) => i.kind === 'position' && i.oz.startsWith(`${prefix}.`) && !i.oz.slice(prefix.length + 1).includes('.'));
  const max = siblings.reduce((m, s) => Math.max(m, Number(s.oz.slice(prefix.length + 1)) || 0), 0);
  return `${prefix}.${String(max + 10).padStart(4, '0')}`;
}
